import ItemCover from "components/ItemCover"
import PageLoader from "components/PageLoader"
import { ProductCardPreview } from "components/ProductCardPreview"
import ProductColors from "components/ProductColors"
import { SearchProductModal } from "components/SearchProductModal"
import { useCurrentId } from "hooks/useCurrentId"
import { useDebounce } from "hooks/useDebounce"
import { ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  IconButton,
  Link,
  LinkButton,
  SingleSelect,
  SingleSelectOption,
  Table,
  Tbody,
  Td,
  TextInput,
  Th,
  Thead,
  Tr,
  Typography,
} from "@strapi/design-system"
import { ArrowLeft, Check, Plus, Trash } from "@strapi/icons"
import { useAlert } from "providers/AlertProvider"

interface LookPageProps {
  add?: boolean
}

export const LookPage = ({ add }: LookPageProps) => {
  const { t } = useTranslation()
  const service = getTintVTOService()
  const navigate = useNavigate()
  const lookId = useCurrentId()
  const [lookItem, setLookItem] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [newProducts, setNewProducts] = useState([])
  const [loadingNewProducts, setLoadingNewProducts] = useState(false)
  const [searchText, setSearchText] = useState("")
  const debouncedSearchText = useDebounce<string>(searchText, 300)
  const [coverType, setCoverType] = useState("Custom Model")
  const [isModalOpen, setModalOpen] = useState(false)
  const { showAlert } = useAlert()
  useEffect(() => {
    const fetchLook = async () => {
      if (lookId && !add) {
        const { data } = await service.lookById(lookId)
        setLookItem(data.look)
      } else {
        setLookItem({
          id: "",
          name: "",
          cover: "",
          products: {
            products: [],
            selectedSkus: [],
          },
        })
      }
    }

    fetchLook()
  }, [lookId, add, service])

  useEffect(() => {
    if (debouncedSearchText === "") {
      setNewProducts([])
    } else {
      const fetchProducts = async () => {
        setLoadingNewProducts(true)
        const { data } = await service.searchProducts(debouncedSearchText)
        setNewProducts(data.searchProducts)
        setLoadingNewProducts(false)
      }

      fetchProducts()
    }
  }, [debouncedSearchText, service])

  const handleSaveItem = async () => {
    setLoading(true)
    const response = await service.saveLook(lookItem)
    const error = response.errors ? response.errors[0] : false

    showAlert({
      error: !!error,
      text: error
        ? error.message
        : t(add ? "lookPage.lookCreated" : "lookPage.lookUpdated", { lookName: lookItem.name }),
    })

    setLoading(false)

    navigate(`/looks`)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name
    setLookItem({
      ...lookItem,
      [name]: e.target.value,
    })
  }

  const addProduct = (p: any, sku: string) => {
    lookItem.products.products.push(p)
    lookItem.products.selectedSkus.push(sku)
    setLookItem({
      ...lookItem,
      products: {
        products: lookItem.products.products,
        selectedSkus: lookItem.products.selectedSkus,
      },
    })
  }

  const deleteProduct = (forDelete: any) => {
    const updatedProducts = lookItem.products.products.filter(
      (p: any) => p.id !== forDelete.productId
    )
    const updatedSkus = lookItem.products.selectedSkus.filter((s: any) => s.id !== forDelete.id)

    setLookItem({
      ...lookItem,
      products: {
        products: updatedProducts,
        selectedSkus: updatedSkus,
      },
    })
  }

  const _handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0]

    if (file) {
      const imageUrl = URL.createObjectURL(file)
      setLookItem({
        ...lookItem,
        lookImageUrl: imageUrl,
        lookImageFile: file,
        file,
        cover: imageUrl,
      })
    }

    e.target.value = ""
  }

  const _handleRemoveImage = () => {
    setLookItem({
      ...lookItem,
      lookImageUrl: "",
      lookImageFile: null,
      file: null,
      cover: null,
    })
  }

  const imagePlaceHolder = "https://placehold.co/400x400?text=Look+Cover+Image"

  return (
    <Box>
      <BaseHeaderLayout
        title={add ? t("lookPage.addLook") : t("lookPage.editLook")}
        as="h1"
        primaryAction={
          <Flex gap={2}>
            <LinkButton variant="secondary" href="/looks" target="_self">
              {t("lookPage.cancel")}
            </LinkButton>
            <Button
              startIcon={<Check />}
              onClick={handleSaveItem}
              loading={loading}
              disabled={loading || !lookItem?.name || lookItem.products.products.length < 2}
            >
              {t("lookPage.save")}
            </Button>
          </Flex>
        }
        navigationAction={
          <Link startIcon={<ArrowLeft />} to="/looks">
            {t("lookPage.backToLooks")}
          </Link>
        }
      />

      <ContentLayout style={{ width: "100%" }}>
        {lookItem ? (
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 1fr",
              gap: "16px",
            }}
          >
            <Box
              padding={8}
              hasRadius
              shadow="tableShadow"
              background="neutral0"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                maxHeight: "340px",
              }}
            >
              <Typography variant="beta">{t("lookPage.lookCardInfo")}</Typography>
              <TextInput
                label={t("lookPage.lookName")}
                name="name"
                onChange={handleInputChange}
                value={lookItem.name}
                required
              />
              <SingleSelect
                label={t("lookPage.coverType")}
                value={coverType}
                onChange={setCoverType}
              >
                <SingleSelectOption value="Custom Model">
                  {t("lookPage.customModel")}
                </SingleSelectOption>
                <SingleSelectOption value="Other Type">
                  {t("lookPage.otherType")}
                </SingleSelectOption>
              </SingleSelect>
              {/* <UploadImageCard
                title="Look Cover"
                src={lookItem.cover}
                onChange={(file) =>
                  setLookItem({
                    ...lookItem,
                    file,
                  })
                }
                required
              /> */}
              {/* <ImageUpload
                imageUrl={lookItem.lookImageUrl}
                imageFile={lookItem.lookImageFile}
                onFileChange={handleFileChange}
                onRemoveImage={handleRemoveImage}
              /> */}
            </Box>

            <Box
              padding={8}
              hasRadius
              shadow="tableShadow"
              background="neutral0"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "calc(100vw - 980px)",
                minWidth: "400px",
                height: "calc(100vh - 200px)",
                overflowY: "scroll",
              }}
            >
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Typography variant="beta">{t("lookPage.lookProductItems")}</Typography>
                <Button
                  style={{ marginLeft: "auto" }}
                  startIcon={<Plus />}
                  variant="secondary"
                  onClick={() => setModalOpen(true)}
                >
                  {t("lookPage.addProduct")}
                </Button>
              </Box>
              <Table>
                <Thead>
                  <Tr>
                    <Th>{t("lookPage.logo")}</Th>
                    <Th>{t("lookPage.name")}</Th>
                    <Th>{t("lookPage.type")}</Th>
                    <Th>{t("lookPage.colors")}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {lookItem.products.selectedSkus.map((sku: any, index: number) => {
                    const product = lookItem.products.products.find(
                      (p: any) => p.id === sku.productId
                    )
                    return (
                      <Tr key={index}>
                        <Td>
                          <Box padding={1}>
                            <ItemCover cover={product?.cover} />
                          </Box>
                        </Td>
                        <Td
                          style={{
                            maxWidth: "178px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {product?.name}
                        </Td>
                        <Td>{product?.type}</Td>
                        <Td>
                          <ProductColors product={{ skus: [sku] }} size={32} />
                        </Td>
                        <Td>
                          <IconButton
                            onClick={() => deleteProduct(sku)}
                            label={t("lookPage.delete")}
                            icon={<Trash />}
                          />
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>

            <Box
              padding={8}
              hasRadius
              shadow="tableShadow"
              background="neutral0"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                justifyContent: "center",
                alignItems: "center",
                height: "calc(100vh - 200px)",
                minHeight: "400px",
              }}
            >
              <ProductCardPreview
                imageSrc={lookItem.cover ? lookItem.cover : imagePlaceHolder}
                sku=""
                brand=""
                productName={lookItem?.name ?? ""}
                color=""
              />
              {/* <Preview productList={lookItem.products.products} onColorChange={() => {}} /> */}
            </Box>

            <SearchProductModal
              isModalOpen={isModalOpen}
              setModalOpen={setModalOpen}
              newProducts={newProducts}
              loadingNewProducts={loadingNewProducts}
              debouncedSearchText={debouncedSearchText}
              searchText={searchText}
              setSearchText={setSearchText}
              addProduct={addProduct}
            />
          </Box>
        ) : (
          <PageLoader />
        )}
      </ContentLayout>
    </Box>
  )
}
